/**
 * api-client.ts — Typed fetch wrapper for the Maloum CRM NestJS API (CORE chat data layer).
 *
 * Relocated VERBATIM from the chatter app's lib/api-client.ts (Phase 12-02, D-01/D-01a) —
 * the fetchJson primitive, credentials:'include', and /api base are unchanged (T-04-33/T-04-37).
 * Chatter-only clients (operatorsApi self-service lifecycle, transactionsApi Surface B,
 * broadcastsApi mass-messages) are NOT here — they stay in
 * the chatter app's lib/api-client.ts, built from the get/post/put/patch/del primitives
 * re-exported below, so this package never imports them (Phase 12-02 design note).
 *
 * Security (T-04-37 / T-04-36):
 *   - credentials: 'include' sends the httpOnly session cookie on every request
 *   - No Maloum URL — only our gateway (T-04-33)
 *   - GET /inbox is server-scoped; the client cannot widen account scope (D-08)
 *   - Send requires confirmed DE preview — never a silent raw-EN send (ARCH §8.1)
 */

import type {
  LoginDto,
  MeDto,
  InboxConversationDto,
  InboxListDto,
  HandoffUpdateDto,
  ChatterDto,
} from '@crm/types';

// ---------------------------------------------------------------------------
// Base
// ---------------------------------------------------------------------------

/** The API base URL — empty string so /api proxied by Vite dev server or Nginx */
const BASE = '/api';

/** NestJS error shape from SafeExceptionFilter / built-in exception filter */
interface ApiError {
  statusCode: number;
  message: string | string[];
  error?: string;
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(
      typeof body.message === 'string'
        ? body.message
        : body.message.join('; '),
    );
    this.name = 'ApiRequestError';
  }
}

async function fetchJson<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...opts.headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    ...opts,
  });

  if (!res.ok) {
    let errorBody: ApiError;
    try {
      errorBody = (await res.json()) as ApiError;
    } catch {
      errorBody = { statusCode: res.status, message: res.statusText };
    }
    throw new ApiRequestError(res.status, errorBody);
  }

  // Success with no body: 204 No Content, or any empty-body success — e.g. a
  // void handler that NestJS serves as 201 with no content (mute/block/report,
  // markRead, handoff, logout, …). Read as text first so an empty body doesn't
  // make res.json() throw "Unexpected end of JSON input", which previously
  // surfaced as a false "…failed" error even though the request succeeded.
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

// get/post/put/patch/del are exported (not just fetchJson) so that
// the chatter app's lib/api-client.ts can build its chatter-only clients
// (operatorsApi, transactionsApi, broadcastsApi) on the identical primitive
// without duplicating fetchJson or re-introducing those clients into @crm/chat.
export const get = <T>(path: string, opts?: RequestInit) =>
  fetchJson<T>('GET', path, undefined, opts);

export const post = <T>(path: string, body?: unknown, opts?: RequestInit) =>
  fetchJson<T>('POST', path, body, opts);

export const put = <T>(path: string, body?: unknown, opts?: RequestInit) =>
  fetchJson<T>('PUT', path, body, opts);

export const patch = <T>(path: string, body?: unknown, opts?: RequestInit) =>
  fetchJson<T>('PATCH', path, body, opts);

export const del = <T>(path: string, opts?: RequestInit) =>
  fetchJson<T>('DELETE', path, undefined, opts);

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const authApi = {
  login: (dto: LoginDto) => post<MeDto>('/auth/login', dto),
  logout: () => post<void>('/auth/logout'),
  refresh: () => post<void>('/auth/refresh'),
  me: () => get<MeDto>('/auth/me'),
};

// ---------------------------------------------------------------------------
// Inbox (server-scoped — chatters see only assigned accounts — D-08)
// ---------------------------------------------------------------------------

/** Summary of an on-demand live inbox refresh (POST /inbox/:accountId/refresh). */
export interface InboxRefreshResult {
  accountId: string;
  refreshed: boolean;
  chatsScanned: number;
  chatsChanged: number;
  messagesUpserted: number;
  translated: number;
  reason?: string;
}

/**
 * Server-side inbox filter (owner 2026-07-11): 'unread' | 'unanswered' — runs in the
 * API's WHERE clause, so it searches the WHOLE account, not just the loaded page.
 */
export type InboxFilter = 'unread' | 'unanswered';

/** Chip state for the list UI: 'all' (no server filter) or one of the server-side filters. */
export type InboxFilterMode = 'all' | InboxFilter;

export const inboxApi = {
  /**
   * GET /inbox — unified inbox (server-scoped; chatters see only assigned accounts).
   * The client cannot widen the scope — scoping is server-side (T-04-37).
   */
  list: (cursor?: string, limit = 50, filter?: InboxFilter) => {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    qs.set('limit', String(limit));
    if (filter) qs.set('filter', filter);
    return get<InboxListDto>(`/inbox?${qs.toString()}`);
  },
  /** GET /inbox/:accountId — per-account inbox (404 if not assigned — D-08) */
  forAccount: (accountId: string, cursor?: string, limit = 50, filter?: InboxFilter) => {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    qs.set('limit', String(limit));
    if (filter) qs.set('filter', filter);
    return get<InboxListDto>(`/inbox/${accountId}?${qs.toString()}`);
  },
  /**
   * POST /inbox/:accountId/refresh — pull this account's inbox live from Maloum.
   * Fired on creator-tab open/switch so new chats + messages appear (owner, 2026-06-28).
   */
  refresh: (accountId: string) => post<InboxRefreshResult>(`/inbox/${accountId}/refresh`),
};

// ---------------------------------------------------------------------------
// Conversations
// ---------------------------------------------------------------------------

export interface ConversationDetailDto {
  id: string;
  accountId: string;
  maloumChatId: string;
  fanId: string | null;
  unread: number;
  lastMessageAt: string | null;
  handoffStatus: string;
  isFanBlocked: boolean;
  isFanReported: boolean;
  /** Passive collision indicator — the chatter ID who last viewed/claimed this conversation (D-05-18). */
  claimed_by_chatter_id?: string | null;
  claimed_at?: string | null;
}

/** URL-stripped media descriptor; bytes fetched via the EU media-proxy. */
export interface MessageMediaDto {
  uploadId: string;
  type: string;
  width: number | null;
  height: number | null;
  durationSec: number | null;
  hasThumbnail: boolean;
  /** true when a full-size source exists → the item is openable in the lightbox. */
  hasFull: boolean;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  maloumMessageId: string;
  direction: string;
  displayText: string | null;
  translationUnavailable: boolean;
  originalText: string | null;
  originalLang: string | null;
  translationStatus: string;
  msgType: string;
  sentAt: string;
  isBackfill: boolean;
  isPpv: boolean;
  ppvUnlocked: boolean;
  priceAmountMinor: number | null;
  priceCurrency: string | null;
  sentByChattterId: string | null;
  /**
   * Display name of the operator who sent this message through the CRM (null for
   * inbound / natively-authored outbound → caption falls back to "You").
   * Optional for deploy skew — an older API omits it.
   */
  sentByName?: string | null;
  /** Media attachments (media / chat_product). Empty for text messages. */
  media: MessageMediaDto[];
}

/**
 * Display state of an in-flight outbound send (outbound_send state machine):
 * confirmed = queued for the worker · sending = POSTing to Maloum ·
 * sent = Maloum ACKed but not yet reconciled into the thread · send_failed = terminal.
 */
export type PendingSendState = 'confirmed' | 'sending' | 'sent' | 'send_failed';

/** An outbound send with no materialized message row yet — rendered as a pending bubble. */
export interface PendingSendDto {
  sendId: string;
  conversationId: string;
  state: PendingSendState;
  originalEn: string;
  /** Approved German text (equals originalEn for English-mode sends). */
  previewDe: string | null;
  isPpv: boolean;
  priceAmountMinor: number | null;
  priceCurrency: string | null;
  /** true when the send carried ≥1 vault media attachment (must be re-attached on retry). */
  hasMedia: boolean;
  /** Number of vault media attachments on this send (multi-media; optional for deploy skew). */
  mediaCount?: number;
  /** Display name of the operator who authored this send (optional for deploy skew). */
  sentByName?: string | null;
  /**
   * Why a send_failed send failed — shown under the "Failed — not delivered" bubble
   * (optional for deploy skew; an older API omits it). Null/absent for non-failed rows.
   */
  failureReason?: string | null;
  createdAt: string;
}

export interface MessagesListDto {
  messages: MessageDto[];
  hasMore: boolean;
  nextCursor?: string;
  /** Optional for deploy skew — an older API omits it. Treat missing as []. */
  pendingSends?: PendingSendDto[];
}

/** Result of POST /conversations/:id/claim (passive collision indicator, D-05-18). */
export interface ConversationClaimDto {
  id: string;
  claimed_by_chatter_id: string | null;
  claimed_at: string | null;
}

/** Result of POST /conversations/resolve (Notifications tap-to-thread). */
export interface ResolveConversationResultDto {
  conversationId: string | null;
  status: 'resolved' | 'unavailable' | 'not_found';
  reason?: string;
}

export const conversationsApi = {
  get: (id: string) => get<ConversationDetailDto>(`/conversations/${id}`),
  messages: (id: string, cursor?: string, limit = 50) => {
    const qs = new URLSearchParams();
    if (cursor) qs.set('cursor', cursor);
    qs.set('limit', String(limit));
    return get<MessagesListDto>(`/conversations/${id}/messages?${qs.toString()}`);
  },
  markRead: (id: string) => post<void>(`/conversations/${id}/read`),
  /** POST /conversations/:id/unread — CRM-local mark-unread (Phase 10, audited; no Maloum call) */
  markUnread: (id: string, accountId: string) =>
    post<void>(`/conversations/${id}/unread?accountId=${encodeURIComponent(accountId)}`),
  setHandoff: (id: string, dto: HandoffUpdateDto) =>
    post<void>(`/conversations/${id}/handoff`, dto),
  /**
   * POST /conversations/:id/claim — passive collision claim (D-05-18).
   *
   * Sets claimed_by_chatter_id on the conversation. Returns the updated claim info.
   * This is passive: no hard lock, other chatters can still open the conversation.
   */
  claim: (id: string) => post<ConversationClaimDto>(`/conversations/${id}/claim`),
  /**
   * POST /conversations/resolve { accountId, fanMemberId } — resolve/open a chat from a
   * fan member id (Notifications tap-to-thread, owner 2026-07-03). Returns an openable
   * conversation id when found (existing thread, or live-resolved when the spine is armed);
   * status:'unavailable' when live sync is disabled/blocked; 'not_found' when the fan has
   * no resolvable chat. Account-scoped server-side (404 for unassigned).
   */
  resolve: (accountId: string, fanMemberId: string) =>
    post<ResolveConversationResultDto>('/conversations/resolve', { accountId, fanMemberId }),
  /**
   * DELETE /conversations/:id/messages/:messageId — "Delete this message" for a DELIVERED
   * outbound message. CRM-local hide is immediate; the Maloum-side delete is spine-gated
   * (disarmed by default). 204 No Content.
   */
  deleteMessage: (id: string, messageId: string) =>
    del<void>(`/conversations/${id}/messages/${messageId}`),
  /**
   * DELETE /conversations/:id/sends/:sendId — "Delete this message" for a pending/failed send.
   * CRM-local delete; aborts a still-queued Maloum POST. 204 No Content.
   */
  deleteSend: (id: string, sendId: string) =>
    del<void>(`/conversations/${id}/sends/${sendId}`),
};

// ---------------------------------------------------------------------------
// Broadcasts (mass messages) — chatter self-service (owner decision 2026-07-02).
//
// Relocated into @crm/chat (2026-07-20) so BOTH the chatter surface and the admin
// full-parity chat surface consume ONE implementation (no fork). The chatter app's
// lib/api-client.ts now re-exports these. Every route is account-scoped server-side
// (assertAccountScope → 404 for unassigned chatters; admins bypass). The real Maloum
// send stays behind SEND_LIVE_ARMED + the CSAM guard; free (unpriced) media additionally
// requires FREE_MEDIA_SEND_ARMED.
// ---------------------------------------------------------------------------

export interface CampaignDto {
  id: string;
  accountId: string;
  createdBy: string;
  approvedBy: string | null;
  approvedAt: string | null;
  name: string;
  messageEn: string;
  messageDe: string | null;
  isPpv: boolean;
  ppvPriceMinor: number | null;
  ppvPriceCurrency: string | null;
  messageKind: string;
  status: string;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  pausedAt: string | null;
  completedAt: string | null;
  etaSeconds: number | null;
  createdAt: string;
}

export interface CreateCampaignBody {
  /** Optional — the server derives an internal label from the message when absent. */
  name?: string;
  messageEn: string;
  accountId: string;
  includeLists?: string[];
  excludeLists?: string[];
  scheduledAt?: string | null;
  messageKind?: 'text' | 'free_media' | 'chat_product';
  recipientCap?: number;
  targetTags?: string[];
  minSpendEur?: number;
  isPpv?: boolean;
  ppvPriceMinor?: number;
  ppvPriceCurrency?: string;
  vaultMediaId?: string;
  vaultMediaMeta?: { type: 'picture' | 'video'; width: number; height: number };
}

/** A Maloum fan segment / list row returned by GET /broadcasts/chat-lists. */
export interface MaloumListDto {
  id: string;
  name: string;
  friendlyName: string;
  isManaged: boolean;
  totalMemberCount: number;
}

export const broadcastsApi = {
  list: (accountId: string) =>
    get<CampaignDto[]>(`/broadcasts?accountId=${encodeURIComponent(accountId)}`),
  get: (id: string) => get<CampaignDto>(`/broadcasts/${id}`),
  create: (body: CreateCampaignBody) => post<CampaignDto>('/broadcasts', body),
  approve: (id: string) => post<CampaignDto>(`/broadcasts/${id}/approve`),
  pause: (id: string, reason?: string) =>
    post<CampaignDto>(`/broadcasts/${id}/pause`, { reason }),
  resume: (id: string) => post<CampaignDto>(`/broadcasts/${id}/resume`),
  cancel: (id: string) => post<void>(`/broadcasts/${id}/cancel`),
  /** Soft-delete: hides the campaign from the list; cancels first when still running. */
  delete: (id: string) => del<void>(`/broadcasts/${id}`),
  getChatLists: (accountId: string) =>
    get<MaloumListDto[]>(`/broadcasts/chat-lists?accountId=${encodeURIComponent(accountId)}`),
};

// ---------------------------------------------------------------------------
// Messages (translate-preview + confirmed send + retranslate)
// ---------------------------------------------------------------------------

export interface TranslatePreviewDto {
  conversationId: string;
  originalEn: string;
  /** outbound_send row id created in 'previewed' state */
  sendId: string;
  /** the EN→DE preview text (German) — server field is previewDe */
  previewDe: string;
  /** 'ok' on success; the server throws 422 on failure (handled in onError) */
  translationStatus: 'ok' | 'failed';
}

export interface SendResultDto {
  messageId: string;
  status: 'queued' | 'sent' | 'failed';
}

export const messagesApi = {
  /**
   * POST /conversations/:id/translate-preview
   *
   * Returns the EN→DE preview. If translationFailed=true, the Send button
   * must be hard-blocked — no raw-EN path (ARCH §8.1, T-04-36).
   */
  translatePreview: (conversationId: string, originalEn: string) =>
    post<TranslatePreviewDto>(`/conversations/${conversationId}/translate-preview`, {
      originalEn,
    }),

  /**
   * POST /conversations/:id/messages — confirmed send (regular or PPV).
   *
   * The chatter MUST have confirmed the DE preview before calling this (D-12).
   * price is optional: absent = regular send, present = PPV send.
   * vaultMediaId: the durable uploadId of the attached vault media (PPV only; D-05-11/14).
   * vaultMediaMeta: type/width/height for the PPV chat_product body.
   * Never a silent send — the UI enforces preview confirmation.
   */
  confirmSend: (
    conversationId: string,
    opts: {
      approvedDe: string;
      clientSendId: string;
      originalEn?: string;
      price?: { amount_minor: number; currency: string };
      /** Vault media uploadId (durable id — NOT the signed URL). Legacy single-media field. */
      vaultMediaId?: string;
      /** Media metadata for the PPV chat_product body. Legacy single-media field. */
      vaultMediaMeta?: { type: string; width: number; height: number };
      /**
       * Multi-media attachment set (migration 0030) — every vault media on this send. When present
       * it supersedes the single vaultMediaId/vaultMediaMeta. uploadId is the durable id.
       */
      vaultMedia?: Array<{ uploadId: string; type: string; width: number; height: number }>;
      /** D-FREE-MEDIA — explicit free-gift intent for a no-price media send (server-enforced). */
      confirmFree?: boolean;
      /** ③ — outbound_send.id of the translate-preview being approved (required for German-mode text). */
      previewSendId?: string;
      /** Migration 0014 — English-mode send (no translation); only honoured for English-mode fans. */
      untranslated?: boolean;
    },
  ) =>
    post<SendResultDto>(`/conversations/${conversationId}/messages`, opts),

  /** POST /messages/:id/retranslate — retry failed incoming translation */
  retranslate: (messageId: string) =>
    post<{ queued: boolean }>(`/messages/${messageId}/retranslate`),
};

// ---------------------------------------------------------------------------
// Fans — notes / nickname / tags / block / unblock / report (D-10, D-17)
// ---------------------------------------------------------------------------

export interface FanProfileDto {
  memberId: string;
  accountId: string;
  username: string | null;
  nickname: string | null;
  notes: string | null;
  tags: string[];
  /** D-13: per-fan spend visible to chatters — NOT aggregate revenue */
  totalSpendForCreator: number | null;
  isBlocked: boolean;
  isReported: boolean;
  /** CRM-local mute/snooze (Phase 6). Reversible; hides the fan from the active queue. */
  isMuted: boolean;
  /**
   * Migration 0014 — per-fan auto-translate mode. true (default) = German (DE⇄EN auto-
   * translation, English sends blocked). false = English (no translation, English sends ok).
   */
  autoTranslate: boolean;
  lastSeenAt: string | null;
}

export const fansApi = {
  get: (memberId: string, accountId: string) =>
    get<FanProfileDto>(`/fans/${memberId}?accountId=${accountId}`),

  /** PUT /fans/:memberId/notes — handoff carrier (D-10) */
  updateNotes: (memberId: string, accountId: string, chatId: string, notes: string) =>
    put<void>(`/fans/${memberId}/notes?accountId=${accountId}&chatId=${chatId}`, { notes }),

  /** PUT /fans/:memberId/nickname (D-10) */
  updateNickname: (memberId: string, accountId: string, chatId: string, nickname: string) =>
    put<void>(`/fans/${memberId}/nickname?accountId=${accountId}&chatId=${chatId}`, { nickname }),

  /** PUT /fans/:memberId/tags — free-form, no curated vocab (D-10) */
  updateTags: (memberId: string, accountId: string, chatId: string, tags: string[]) =>
    put<void>(`/fans/${memberId}/tags?accountId=${accountId}&chatId=${chatId}`, { tags }),

  /** POST /fans/:memberId/block (D-17, audited) */
  block: (memberId: string, accountId: string) =>
    post<void>(`/fans/${memberId}/block?accountId=${accountId}`),

  /** POST /fans/:memberId/unblock (D-17, audited) */
  unblock: (memberId: string, accountId: string) =>
    post<void>(`/fans/${memberId}/unblock?accountId=${accountId}`),

  /** POST /fans/:memberId/report (D-17, audited) */
  report: (memberId: string, accountId: string) =>
    post<void>(`/fans/${memberId}/report?accountId=${accountId}`),

  /** POST /fans/:memberId/mute — CRM-local mute/snooze (Phase 6, audited; no Maloum call) */
  mute: (memberId: string, accountId: string) =>
    post<void>(`/fans/${memberId}/mute?accountId=${accountId}`),

  /** POST /fans/:memberId/unmute — reverse CRM-local mute (Phase 6, audited) */
  unmute: (memberId: string, accountId: string) =>
    post<void>(`/fans/${memberId}/unmute?accountId=${accountId}`),

  /**
   * POST /fans/:memberId/auto-translate — set the fan's translation mode (migration 0014).
   * enabled=false → English mode (no DE⇄EN auto-translation; English replies sent verbatim).
   * CRM-local, audited; global for this creator+fan across all chatters.
   */
  setAutoTranslate: (memberId: string, accountId: string, enabled: boolean) =>
    post<void>(`/fans/${memberId}/auto-translate?accountId=${accountId}`, { enabled }),
};

// ---------------------------------------------------------------------------
// Vault (F3 Tier-1 read-only browse + fan-aware picker — Plan 05-07)
// ---------------------------------------------------------------------------

// A4 (GDPR/Art.9): the vault DTOs carry NO signed URLs. The Maloum signed Bunny/GCS
// links are Art.9 media on a non-EU CDN — the browser must never fetch them directly.
// Thumbnails render through the same-origin EU media-proxy (vaultApi.*ThumbUrl), keyed
// by the durable uploadId; `hasThumbnail` says whether a poster exists to render.
export interface VaultThumbnailDto {
  uploadId: string;
  type: string;
  width: number;
  height: number;
  /** true when a thumbnail is renderable via the EU proxy. */
  hasThumbnail: boolean;
}

export interface VaultFolderDto {
  id: string;
  name: string;
  isManaged: boolean;
  pictureCount: number;
  videoCount: number;
  thumbnails: VaultThumbnailDto[];
}

export interface VaultFolderPageDto {
  folders: VaultFolderDto[];
  next: string | number | null;
}

export interface VaultMediaItemDto {
  /** Durable id — use as mediaId in PPV send body. */
  uploadId: string;
  uploadStatus: string;
  type: string;
  width: number;
  height: number;
  length?: number;
  /** true when a poster/thumbnail is renderable via the EU proxy. */
  hasThumbnail: boolean;
  /** true when a full-size / playable source exists → openable in the lightbox. */
  hasFull: boolean;
  /** Fan-specific badges (present when ?fanId= was passed). */
  sentForPrice?: number;
  sentForFree?: boolean;
  boughtForPrice?: number;
}

export interface VaultMediaPageDto {
  items: VaultMediaItemDto[];
  next: string | number | null;
}

export const vaultApi = {
  /**
   * GET /vault/folders — list vault folders for the account (scoped by accountId param).
   * Uses ?next= for pagination (D-PAGINATION-PARAM-NEXT).
   */
  folders: (accountId: string, query?: string, next?: string) => {
    const qs = new URLSearchParams();
    qs.set('accountId', accountId);
    if (query) qs.set('query', query);
    if (next) qs.set('next', next);
    return get<VaultFolderPageDto>(`/vault/folders?${qs.toString()}`);
  },

  /**
   * GET /vault/folders/:folderId/media — list media in a folder (fan-aware).
   * Pass fanId to get sentForPrice/boughtForPrice badges (D-05-13).
   * Items carry only uploadId (the durable PPV mediaId) + hasThumbnail — no signed URL.
   */
  media: (accountId: string, folderId: string, fanId?: string, next?: string | number) => {
    const qs = new URLSearchParams();
    qs.set('accountId', accountId);
    if (fanId) qs.set('fanId', fanId);
    if (next !== undefined && next !== null) qs.set('next', String(next));
    return get<VaultMediaPageDto>(`/vault/folders/${folderId}/media?${qs.toString()}`);
  },

  /**
   * Same-origin EU media-proxy path for a folder-strip thumbnail (A4). The API fetches
   * the signed CDN bytes server-side — the browser never touches the non-EU CDN.
   * Render directly as <img src=…> (cookie auth flows on same-origin requests).
   */
  folderThumbUrl: (accountId: string, folderId: string, uploadId: string) =>
    `${BASE}/vault/folders/${encodeURIComponent(folderId)}/thumb/${encodeURIComponent(uploadId)}?accountId=${encodeURIComponent(accountId)}`,

  /** Same-origin EU media-proxy path for a media-item thumbnail (A4). */
  mediaThumbUrl: (accountId: string, folderId: string, uploadId: string) =>
    `${BASE}/vault/folders/${encodeURIComponent(folderId)}/media/${encodeURIComponent(uploadId)}/thumb?accountId=${encodeURIComponent(accountId)}`,

  /**
   * Same-origin EU media-proxy path for a media item's FULL-SIZE bytes (A4) — the
   * picker lightbox for pictures. Videos degrade to their poster here; they play via
   * mediaHlsUrl. Render directly as <img src=…> (cookie auth flows same-origin).
   */
  mediaFullUrl: (accountId: string, folderId: string, uploadId: string) =>
    `${BASE}/vault/folders/${encodeURIComponent(folderId)}/media/${encodeURIComponent(uploadId)}/full?accountId=${encodeURIComponent(accountId)}`,

  /**
   * Same-origin EU media-proxy path for a video's HLS master playlist (A4) — the
   * picker lightbox player. Variant playlists + segments resolve back through the same
   * route (the server rewrites child URIs to `hls?p=…`). Feed to hls.js / native HLS.
   */
  mediaHlsUrl: (accountId: string, folderId: string, uploadId: string) =>
    `${BASE}/vault/folders/${encodeURIComponent(folderId)}/media/${encodeURIComponent(uploadId)}/hls?accountId=${encodeURIComponent(accountId)}`,
};

// ---------------------------------------------------------------------------
// Creators (Phase 05-06: GET /creators/mine — per-creator rollup for chatter tabs)
// ---------------------------------------------------------------------------

/** Summary of a single account within a creator rollup. */
export interface CreatorAccountSummary {
  accountId: string;
  unread: number;
  connectionStatus: string;
  lastMessageAt: string | null;
}

/**
 * CreatorRollupDto — one entry per assigned creator from GET /creators/mine.
 *
 * D-05-01: tab = creator (aggregates multiple maloum_account rows).
 * D-05-03: tabUnread = sum(accounts.unread); tabHealth = worst-of connectionStatus.
 * D-05-04: ordered most-recent-activity first from the server.
 */
export interface CreatorRollupDto {
  creatorId: string;
  creatorName: string;
  accounts: CreatorAccountSummary[];
  /** Sum of all accounts' unread counts (D-05-03). */
  tabUnread: number;
  /** Worst-of connectionStatus across all accounts (D-05-03). */
  tabHealth: string;
  /** ISO string of the most recent lastMessageAt across all accounts (D-05-04). */
  lastActivityAt: string | null;
}

export const creatorsApi = {
  /**
   * GET /creators/mine — per-creator rollup for the authenticated chatter.
   * Assignment-scoped: chatter only sees creators they are assigned to (T-05-20).
   * Ordered most-recently-active first (D-05-04).
   */
  mine: () => get<CreatorRollupDto[]>('/creators/mine'),
};

// ---------------------------------------------------------------------------
// Chatters — list of active operators (for CollisionChip displayName resolution, SC-6)
// ---------------------------------------------------------------------------

export { type ChatterDto };

export const chattersApi = {
  /** GET /chatters — list of active operators with displayName (D-09-16). */
  list: () => get<ChatterDto[]>('/chatters'),
};

// ---------------------------------------------------------------------------
// Chatter Transactions (Surface B — D-07-12..16). Relocated into @crm/chat (2026-07-20)
// so the TransactionsPane ("Notifications") + FanPanel "Recent payments" work in BOTH the
// chatter surface and the admin full-parity chat surface.
//
// INVARIANT (D-07-13): per-transaction € amounts only — NO aggregate totals, NO breakdown,
// NO top-spenders. This is SEPARATE from the admin-only /kpi/* routes.
//
// Operators (self-service lifecycle) stays CHATTER-ONLY in the chatter app's
// lib/api-client.ts — no relocated @crm/chat component consumes it.
// ---------------------------------------------------------------------------

/**
 * Per-transaction record from the chatter-scoped feed (Plan 07-04).
 * Amounts are in MINOR UNITS (grossMinor=500 → €5.00). NO aggregate fields (D-07-13).
 */
export interface ChatterTransactionDto {
  id: string;
  creatorId: string;
  creatorName: string;
  /** maloum_account UUID — used by the Notifications tap-to-thread resolve call. */
  accountId: string;
  occurredAt: string;
  kind: 'subscription' | 'ppv_unlock' | 'tip' | 'other';
  fanUsername: string | null;
  fanMemberId: string | null;
  /**
   * fanConversationId enables tap-to-thread navigation (D-07-16).
   * When null: the row resolves/opens the chat via POST /conversations/resolve.
   */
  fanConversationId: string | null;
  currency: string;
  /** matches server DTO which emits string | null (APPROVED filter makes it non-null in practice). */
  txStatus: string | null;
  /** Gross amount in minor units (cents). */
  grossMinor: number;
  /** Payout/net amount in minor units. */
  payoutMinor: number | null;
}

export interface ChatterTransactionPageDto {
  next: string | null;
  data: ChatterTransactionDto[];
}

export const transactionsApi = {
  /**
   * GET /chatter/transactions?creatorId=&since=&until=&fanMemberId=&limit=&next=
   * Returns the per-creator scoped feed. Server enforces assignment scope for chatters;
   * admins bypass (chatter-transactions.service admin branch). Used by both TransactionsPane
   * and FanPanel's injected fetchRecentPayments.
   */
  chatterFeed: (
    creatorId: string,
    since?: string,
    until?: string,
    fanMemberId?: string,
    limit = 20,
    next?: string,
  ) => {
    const qs = new URLSearchParams({ creatorId, limit: String(limit) });
    if (since) qs.set('since', since);
    if (until) qs.set('until', until);
    if (fanMemberId) qs.set('fanMemberId', fanMemberId);
    if (next) qs.set('next', next);
    return get<ChatterTransactionPageDto>(`/chatter/transactions?${qs.toString()}`);
  },

  /**
   * GET /chatter/transactions/unread-count?creatorId=
   * Single-creator unread count vs the caller's last-viewed marker.
   */
  unreadCount: (creatorId: string) =>
    get<{ count: number }>(`/chatter/transactions/unread-count?creatorId=${encodeURIComponent(creatorId)}`),

  /**
   * GET /chatter/transactions/unread-counts
   * Batch unread-counts map for ALL of the caller's creators (assigned for chatters, all for
   * admins — D-07-15). Takes NO client args. Returns Record<creatorId, unreadCount>.
   */
  unreadCounts: () =>
    get<Record<string, number>>('/chatter/transactions/unread-counts'),

  /**
   * POST /chatter/transactions/mark-seen { creatorId }
   * Marks all transactions for this creator as seen (upserts last_viewed_at).
   */
  markSeen: (creatorId: string) =>
    post<void>('/chatter/transactions/mark-seen', { creatorId }),

  /**
   * GET /chatter/transactions/all?next=&limit= (D-13-03/06, Plan 02)
   * Assignment-scoped merged feed across ALL of the caller's creators. Takes NO creator
   * input — scope is 100% server-derived (getAssignedCreatorIds for chatters, all
   * creators for admin). Backs the merged Notifications view on the "All" tab.
   */
  allChatterFeed: (next?: string, limit = 20) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (next) qs.set('next', next);
    return get<ChatterTransactionPageDto>(`/chatter/transactions/all?${qs.toString()}`);
  },

  /**
   * POST /chatter/transactions/mark-seen-all (D-13-07)
   * Marks all of the caller's assigned creators' transactions as seen in one call — no
   * body, no creator input; scope is 100% server-derived.
   */
  markSeenAll: () => post<void>('/chatter/transactions/mark-seen-all'),
};
